import { Storage } from "aws-amplify";
import React, { useState } from "react";
import styled from "styled-components";

import { assertAppSyncError } from "@examspark/common";
import { getS3KeyPrefixForClassroom } from "@examspark/common/dist/utils/file";
import {
  toast,
  Button,
  Dropzone,
  HSpace,
  Modal,
  ModalProps,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconDownloadLine,
  IconSearchLine,
  IconTrashLine,
  IconUploadLine,
} from "@examspark/common-ui/dist/components/icons";
import { ParsedClassroom } from "@examspark/types";

import { useMutationImportClassroomRoster } from "../../../api/operations/classroom";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { getBucketName, getBucketRegion } from "../../../utils/util";

const StyledContainer = styled.div`
  border: dashed 2px #ccc;
`;
const StyledPlaceholder = styled.div`
  padding: 2rem;
  color: #666;
`;
const StyledPlaceholderIcon = styled.div`
  align-self: center;
`;
const StyledPlaceholderBody = styled.div`
  padding: 1rem;

  & p:first-child {
    margin-top: 0;
  }
  & p:last-child {
    margin-bottom: 0;
  }
`;

const StyledFile = styled(Toolbar)`
  padding: 1rem;
`;

const UpdateRosterModal = ({
  classroom,
  onBack,
  onComplete,
  ...props
}: ModalProps & {
  classroom?: ParsedClassroom;
  onBack?: () => void;
  onComplete?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const workspaceId = classroom?.workspaceId;
  const importRosterMutation = useMutationImportClassroomRoster();
  const nameFieldRef = React.useRef<HTMLInputElement>(null);
  const browseButtonRef = React.useRef<HTMLInputElement>(null);
  const [files, updateFiles] = useFileUpload({
    workspaceId,
    region: getBucketRegion(),
    bucketName: getBucketName(),
  });

  const handleUpload = async () => {
    try {
      console.log("classroom", classroom, files);
      if (!workspaceId) {
        console.error("Invalid workspace");
        return;
      }
      if (files.length === 0) {
        console.warn("No files to upload");
        return;
      }

      setIsLoading(true);
      await Promise.all(
        files.map(async (file, index) => {
          if (!file.id) return;
          if (!classroom.workspaceId) return;
          const fileKeyPrefix = getS3KeyPrefixForClassroom(
            workspaceId,
            classroom,
          );
          const fileKey = `${fileKeyPrefix}/roster.csv`;
          console.log("fileKey", fileKey);

          const result = await Storage.put(fileKey, file.file, {
            progressCallback: (progress: { loaded: number; total: number }) => {
              if (!file.id) return;
              console.log("progress", progress);
              updateFiles.updateFileById(file.id, { progress });
            },
          });

          console.log("completed?", result);

          updateFiles.updateFileById(file?.id, { status: "IN_PROGRESS" });
          await importRosterMutation.mutateAsync({
            workspaceId: classroom.workspaceId,
            classroomId: classroom.id,
            key: `public/${fileKey}`,
          });
        }),
      );
      onComplete?.();
      toast.success("Roster uploaded successfully.");
      updateFiles.clearFiles();
      setIsLoading(false);
    } catch (error) {
      assertAppSyncError(error);
      console.error(error.errors[0].message);
      toast.error(error.errors[0].message);
      setIsLoading(false);
      onComplete?.();
    }
  };

  const handleDrop = (acceptedFiles: File[]) => {
    console.log("acceptedFiles", acceptedFiles);
    updateFiles.clearFiles();
    acceptedFiles.slice(0, 1).forEach(async (file) => {
      updateFiles.addFile(file);
    });
  };

  const disableUpload = !files?.length
    ? true
    : false || updateFiles.isLoading || updateFiles.hasFailures || isLoading;

  return (
    <>
      <Modal
        title="Upload Roster"
        buttons={
          <>
            <Button
              title="Browse Files"
              icon={<IconSearchLine />}
              onClick={() => browseButtonRef.current?.click()}
            >
              Browse Files
            </Button>
            <Button
              title="Upload"
              onClick={handleUpload}
              isLoading={isLoading}
              icon={<IconUploadLine />}
              color="primary"
              disabled={disableUpload}
            >
              Upload
            </Button>
          </>
        }
        onAfterOpen={() => {
          nameFieldRef.current?.focus();
        }}
        onAfterClose={() => {
          updateFiles.clearFiles();
        }}
        {...props}
      >
        <Modal.PaddedBody>
          <VSpace>
            <Dropzone
              allowedFileTypes={["text/csv"]}
              browseButtonRef={browseButtonRef}
              onDrop={(accepted: File[]) => {
                handleDrop(accepted);
              }}
            >
              <StyledContainer>
                {files.length === 0 ? (
                  <StyledPlaceholder>
                    <HSpace>
                      <StyledPlaceholderIcon>
                        <IconDownloadLine width="16" />
                      </StyledPlaceholderIcon>
                      <StyledPlaceholderBody>
                        <p>
                          Drag your roster file CSV into this space, or click
                          Browse Files to locate the file on your device.{" "}
                        </p>
                        <p>
                          Close this window and download your current roster if
                          you need a CSV template to modify and upload.
                        </p>
                      </StyledPlaceholderBody>
                    </HSpace>
                  </StyledPlaceholder>
                ) : (
                  <>
                    {files?.map((f, idx) => {
                      return (
                        <StyledFile key={idx}>
                          <div style={{ overflow: "hidden", flex: 1 }}>
                            {f.file.name}
                          </div>
                          <div>
                            <Button
                              title="Remove File"
                              icon={<IconTrashLine />}
                              onClick={() => updateFiles.clearFiles()}
                            />
                          </div>
                        </StyledFile>
                      );
                    })}
                  </>
                )}
              </StyledContainer>
            </Dropzone>
          </VSpace>
        </Modal.PaddedBody>
      </Modal>
    </>
  );
};

export default UpdateRosterModal;
